import { QueryFunctionOptions } from "@apollo/client"
import useDebouncedValue from "@pathwright/ui/src/components/hooks/useDebouncedValue"
import { flattenEdges, usePaginator } from "@pathwright/utils"
import {
  LibraryResourcesQueryVariables,
  useLibraryResourcesQuery as _useLibraryResourcesQuery
} from "@pathwright/graphql-client/src/generated"

type UseLibraryResourcesQueryHookReturn = ReturnType<
  typeof useLibraryResourcesQuery
>
type UseLibraryResourcesQueryHookProps = LibraryResourcesQueryVariables &
  QueryFunctionOptions

export const useLibraryResourcesQuery = ({
  idFilter,
  licensedFromSchoolIdFilter,
  resourceTypeFilter,
  roleFilter,
  assignConfig,
  first = 24,
  orderBy,
  search: searchProp,
  fetchPolicy = "cache-and-network"
}: UseLibraryResourcesQueryHookProps) => {
  const [search, debouncedSearch, debouncingSearch, handleDebounceSearch] =
    useDebouncedValue(300)

  const libraryResourcesQuery = _useLibraryResourcesQuery({
    variables: {
      idFilter,
      licensedFromSchoolIdFilter,
      resourceTypeFilter,
      roleFilter,
      assignConfig,
      search: searchProp || debouncedSearch,
      first,
      orderBy
    },
    fetchPolicy,
    notifyOnNetworkStatusChange: true
  })

  const { data, previousData, error, refetch } = libraryResourcesQuery

  const { loadMore, hasMore, loadingMore } = usePaginator({
    query: libraryResourcesQuery,
    paginatorValue: libraryResourcesQuery.data?.resources
  })
  const total = data?.resources?.total || 0
  const resources = flattenEdges(data?.resources || previousData?.resources)

  const loading = libraryResourcesQuery.loading && !data

  return {
    libraryResourcesQuery,
    error,
    loading,
    refetch,
    loadMore,
    hasMore,
    loadingMore,
    total,
    resources,
    search,
    debouncedSearch,
    debouncingSearch,
    handleDebounceSearch
  }
}

type ComposedComponentChildren = (
  props: UseLibraryResourcesQueryHookReturn
) => React.ReactNode

export const withLibraryResources =
  (
    ComposedComponent: React.ComponentType<
      UseLibraryResourcesQueryHookReturn & {
        children: ComposedComponentChildren
      }
    >
  ) =>
  (
    props: UseLibraryResourcesQueryHookProps & {
      children: ComposedComponentChildren
    }
  ) => {
    const passProps = useLibraryResourcesQuery(props)
    return <ComposedComponent {...props} {...passProps} />
  }

const LibraryResourcesQuery = withLibraryResources(
  ({ children, ...rest }) => children(rest) || null
)

export default LibraryResourcesQuery
